/**
 * <cart-add-bag>
 *
 * One-tap "add another bag" for the free-shipping nudge in the cart drawer.
 *
 * It increments the existing cart line rather than POSTing a new one to
 * /cart/add.js. Adding looked equivalent but was not: Shopify only merges two
 * lines when the variant AND every line property match exactly, and these lines
 * carry _bundle_group / _bundle_title / _bundle_image. Copying those across by
 * hand produced a near-match, so the cart kept the new bag as a second line
 * inside the bundle: the card read "1 x Smooth Talker" twice instead of "2 x",
 * and the bundle thumbnail dropped out. Incrementing by line key sidesteps the
 * whole class of problem, because there is nothing to match.
 *
 * Follows the same transport contract as cart-upgrade-banner: POST with
 * `sections` and `sections_url` so the Section Rendering API returns fresh drawer
 * HTML in the same round trip. The swap targets `.cart-drawer__inner` rather than
 * the whole section, because <theme-drawer> and its <dialog> sit above that node
 * and replacing the section would tear down the open dialog and slam the drawer
 * shut mid-tap. Every custom element inside the replaced markup is already
 * defined by the time this runs (cart-drawer.liquid deliberately loads those
 * definitions up front), so the re-parsed nodes upgrade immediately.
 */
class CartAddBag extends HTMLElement {
  connectedCallback() {
    this.handleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  get button() {
    return this.querySelector('[data-add-bag-button]');
  }

  get payload() {
    const source = this.querySelector('[data-add-bag-payload]');
    if (!source || !source.textContent) return null;
    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      console.warn('[cart-add-bag] could not parse payload:', error);
      return null;
    }
  }

  get changeUrl() {
    return (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) || '/cart/change.js';
  }

  /**
   * Ask for every section that renders a cart count, not just the drawer: the
   * header bubble reads from its own section and would otherwise sit stale until
   * the next navigation.
   */
  get sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    document.querySelectorAll('[id^="shopify-section-"]').forEach((el) => {
      const id = el.id.replace('shopify-section-', '');
      if (id.includes('header') || id.includes('cart')) ids.add(id);
    });
    return Array.from(ids);
  }

  async handleClick(event) {
    if (!event.target.closest('[data-add-bag-button]')) return;
    event.preventDefault();
    if (this.hasAttribute('data-busy')) return;

    const payload = this.payload;
    if (!payload || !payload.key) return;

    const button = this.button;
    this.setAttribute('data-busy', '');
    if (button) button.setAttribute('disabled', '');

    try {
      const response = await fetch(this.changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: payload.key,
          quantity: (Number(payload.quantity) || 1) + 1,
          sections: this.sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || data.errors || 'Could not add the bag');
      }

      this.applySections(data.sections);
    } catch (error) {
      console.error('[cart-add-bag] failed to add the bag:', error);
      if (button) button.removeAttribute('disabled');
      this.removeAttribute('data-busy');
    }
  }

  applySections(sections) {
    if (!sections) return;
    const parser = new DOMParser();

    Object.entries(sections).forEach(([id, html]) => {
      if (typeof html !== 'string') return;
      const container = document.getElementById('shopify-section-' + id);
      if (!container) return;
      const next = parser.parseFromString(html, 'text/html');

      const inner = container.querySelector('.cart-drawer__inner');
      const nextInner = next.querySelector('.cart-drawer__inner');
      if (inner && nextInner) {
        inner.innerHTML = nextInner.innerHTML;
        return;
      }

      // Not the drawer, so the only thing that changed is the count bubble.
      const bubble = container.querySelector('[class*="cart-bubble"], [class*="cart-count"]');
      const nextBubble = next.querySelector('[class*="cart-bubble"], [class*="cart-count"]');
      if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
    });
  }
}

if (!customElements.get('cart-add-bag')) {
  customElements.define('cart-add-bag', CartAddBag);
}
